import Modal from "./components/Modal";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "../../../../components/Translation";
import { tarjetas } from "../../../../constants/laboratory/cards";

const AUTOPLAY_DELAY = 5000;

export default function Cards() {
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Autoplay: avanza el featured cada AUTOPLAY_DELAY ms
  const startAutoplay = () => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      setFeaturedIndex(prev => (prev + 1) % tarjetas.length);
    }, AUTOPLAY_DELAY);
  };

  const stopAutoplay = () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  };

  useEffect(() => {
    if (isPlaying) {
      startAutoplay();
    } else {
      stopAutoplay();
    }
    return () => stopAutoplay();
  }, [isPlaying]);

  // Cuando cambia el featured, hacer scroll al item activo en el sidebar
  useEffect(() => {
    if (!sidebarRef.current) return;
    const activeEl = sidebarRef.current.querySelector(`[data-index="${featuredIndex}"]`) as HTMLElement;
    if (activeEl) {
      const sidebar = sidebarRef.current;
      const elTop = activeEl.offsetTop;
      const elBottom = elTop + activeEl.offsetHeight;
      const sidebarTop = sidebar.scrollTop;
      const sidebarBottom = sidebarTop + sidebar.offsetHeight;

      if (elTop < sidebarTop || elBottom > sidebarBottom) {
        sidebar.scrollTo({ top: elTop - 8, behavior: "smooth" });
      }
    }
  }, [featuredIndex]);

  const handleSidebarClick = (index: number) => {
    if (index === featuredIndex) {
      // Ya está en featured → abre modal
      stopAutoplay();
      setIsPlaying(false);
      setOpenModalIndex(index);
    } else {
      // No está en featured → lo promueve a featured y reinicia autoplay
      setFeaturedIndex(index);
      if (isPlaying) startAutoplay();
    }
  };

  const handleFeaturedClick = () => {
    stopAutoplay();
    setIsPlaying(false);
    setOpenModalIndex(featuredIndex);
  };

  const closeModal = () => {
    setOpenModalIndex(null);
    setIsPlaying(true);
  };

  const featured = tarjetas[featuredIndex];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-dorado-uady text-micro font-bold uppercase tracking-widest mb-1">
              RESEARCH HIGHLIGHTS
            </p>
            <h2 className="text-3xl font-bold text-azul-uady">
              {t("laboratorio.trabajo")}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Play/pause */}
            <button
              onClick={() => setIsPlaying(p => !p)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-dorado-uady hover:text-white transition-colors"
              aria-label={isPlaying ? "Pausar rotación" : "Reanudar rotación"}
            >
              {isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <a
              href="/researchLines"
              className="hidden md:flex items-center gap-2 text-mini text-gray-500 hover:text-dorado-uady transition-colors"
            >
              Ver todos los proyectos →
            </a>
          </div>
        </div>

        {/* Layout desktop: featured izquierda + sidebar derecha */}
        <div className="hidden lg:grid lg:grid-cols-[1fr_300px] gap-6">

          {/* Featured card */}
          <div
            className="group relative rounded-xl overflow-hidden cursor-pointer min-h-[420px]"
            onClick={handleFeaturedClick}
            title="Click para ver detalles"
          >
            <img
              key={featuredIndex}
              src={featured.image}
              alt={featured.subtitle}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

            {/* Progress bar autoplay */}
            {isPlaying && (
              <div className="absolute bottom-0 left-0 h-1 bg-dorado-uady animate-progress w-full origin-left" />
            )}

            <div className="absolute bottom-0 left-0 p-6">
              <span className="bg-dorado-uady text-white text-micro font-semibold px-2 py-1 rounded uppercase mb-3 inline-block">
                {t(`laboratorio.${featured.category}`)}
              </span>
              <h3 className="text-white text-xl font-bold mb-2">
                {t(`laboratorio.${featured.subtitle}`)}
              </h3>
              <p className="text-white/70 text-mini line-clamp-2">
                {t(`laboratorio.${featured.text}`)}
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-dorado-uady text-mini font-semibold">
                {t("laboratorio.public_completa")} →
              </span>
            </div>
          </div>

          {/* Sidebar con scroll */}
          <div
            ref={sidebarRef}
            className="flex flex-col gap-3 overflow-y-auto max-h-[420px] pr-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
          >
            {tarjetas.map((tarjeta, index) => {
              const isActive = index === featuredIndex;
              return (
                <div
                  key={index}
                  data-index={index}
                  onClick={() => handleSidebarClick(index)}
                  className={`group flex gap-3 p-3 rounded-xl cursor-pointer transition-all border ${isActive
                    ? "border-dorado-uady bg-dorado-uady/5 shadow-sm"
                    : "border-gray-100 bg-gray-50 hover:border-dorado-uady/50 hover:shadow-sm"
                    }`}
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={tarjeta.image}
                      alt={tarjeta.subtitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-micro font-bold uppercase mb-0.5 ${isActive ? "text-dorado-uady" : "text-gray-400"}`}>
                      {t(`laboratorio.${tarjeta.category}`)}
                    </p>
                    <h4 className="text-mini font-semibold text-azul-uady line-clamp-2">
                      {t(`laboratorio.${tarjeta.subtitle}`)}
                    </h4>
                    {isActive && (
                      <p className="text-micro text-gray-400 mt-0.5">
                        Click para ver detalles
                      </p>
                    )}
                  </div>
                  {/* Indicador activo */}
                  {isActive && (
                    <div className="w-1 rounded-full bg-dorado-uady self-stretch flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: carrusel horizontal */}
        <div className="lg:hidden flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin">
          {tarjetas.map((tarjeta, index) => (
            <div
              key={index}
              className="snap-start flex-shrink-0 w-72 rounded-xl overflow-hidden bg-[#0d1f35] shadow-lg cursor-pointer"
              onClick={() => {
                if (index === featuredIndex) {
                  setOpenModalIndex(index);
                } else {
                  setFeaturedIndex(index);
                }
              }}
            >
              <div className="h-44 overflow-hidden relative">
                <img
                  src={tarjeta.image}
                  alt={tarjeta.subtitle}
                  className="h-full w-full object-cover"
                />
                <span className="absolute top-2 left-2 bg-dorado-uady text-white text-micro font-semibold px-2 py-0.5 rounded uppercase">
                  {t(`laboratorio.${tarjeta.category}`)}
                </span>
              </div>
              <div className="p-4">
                <h3 className="text-white text-mini font-semibold mb-1 line-clamp-2">
                  {t(`laboratorio.${tarjeta.subtitle}`)}
                </h3>
                <p className="text-white/60 text-micro line-clamp-2">
                  {t(`laboratorio.${tarjeta.text}`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {tarjetas.map((tarjeta, index) => (
        <Modal
          key={index}
          isOpen={openModalIndex === index}
          onClose={closeModal}
          content={tarjeta.modalContent}
          modalId={`modal${index + 1}`}
        />
      ))}
    </section>
  );
}